import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import CountryStore from '../../../store/CountryStore';
import OrderStore from '../../../store/OrderStore';
import UIStore from '../../../store/UIStore';
import OrderTotalClass from '../../../types/order/OrderTotalClass';
import Paths from '../../../types/Paths';
import HelperNavigation from '../../common/HelperNavigation';
import Icon from '../../common/Icon';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import ContentForState from '../../loader/ContentForState';
import UnexpectedError from '../../loader/UnexpectedError';
import OrderCloneButton from '../../order/OrderCloneButton';
import OrderStatusLabel from '../../order/OrderStatusLabel';

import styles from '../../../styles/_exports.scss';
import Price from '../../product/Price';
import MyAccountOrderProducts from '../MyAccountOrderProducts';

const messages = defineMessages({
  'myAccount.orderTitle': {
    id: 'myAccount.orderTitle',
    defaultMessage: 'Order {orderId}',
  },
});

const billingInfoTotals = [
  OrderTotalClass.PRODUCTS_TOTAL,
  OrderTotalClass.SHIPPING_TOTAL,
  OrderTotalClass.TAX_FREE_TOTAL,
  OrderTotalClass.TAX_TOTAL,
  OrderTotalClass.TOTAL,
];

export const OrderHeaders = {
  image: <FormattedMessage {...globalTranslations.productTitle} />,
  name: <FormattedMessage {...globalTranslations.productNameTitle} />,
  quantity: <FormattedMessage {...globalTranslations.quantityTitle} />,
  deliveredQuantity: (
    <FormattedMessage
      id="order.deliveredQuantityTitle"
      defaultMessage="Delivered qty"
    />
  ),
  unitPrice: <FormattedMessage {...globalTranslations.unitPriceTitle} />,
  totalSum: <FormattedMessage {...globalTranslations.totalPriceSentence} />,
};

const StatusHeaders = {
  date: <FormattedMessage id="order.dateTitle" defaultMessage="Date" />,
  status: <FormattedMessage {...globalTranslations.statusTitle} />,
  description: (
    <FormattedMessage
      id="order.statusDescriptionTitle"
      defaultMessage="Description"
    />
  ),
};

const QueryStatus = {
  NONE: 'NONE',
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  NOT_FOUND: 'NOT_FOUND',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
};

const MyAccountOrder = ({
  accountStore,
  configStore,
  countryStore,
  orderStore,
  uiStore,
  intl,
  routeService,
  code,
}) => {
  const [queryStatus, setQueryStatus] = useState(QueryStatus.NONE);
  const params = useParams();

  useEffect(() => {
    loadOrder();
  }, []);

  const getCurrentOrderId = () => {
    return params.id;
  };

  const loadOrder = () => {
    setQueryStatus(QueryStatus.LOADING);

    orderStore
      .loadOrder(getCurrentOrderId(), code)
      .then(() => {
        setQueryStatus(QueryStatus.LOADED);
      })
      .catch((e) => {
        const status = e.response ? e.response.status : 500;
        const errorMap = {
          404: QueryStatus.NOT_FOUND,
          500: QueryStatus.UNEXPECTED_ERROR,
        };
        setQueryStatus(errorMap[status]);
      });
  };

  const getOrder = () => {
    return orderStore.orders.get(getCurrentOrderId());
  };

  const getBreadcrumbsPath = () => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount,
      },
      {
        text: intl.formatMessage(globalTranslations.myOrderHistoryTitle),
        url: Paths.MyAccountOrderList,
      },
      {
        text: intl.formatMessage(messages['myAccount.orderTitle'], {
          orderId: getCurrentOrderId(),
        }),
        url: Paths.MyAccountOrderList,
      },
    ]);
  };

  const renderPaymentAddressInfo = (address) => {
    return (
      <>
        <h5
          className="MyAccountOrder__detail-header"
          style={{
            fontWeight: styles['font-weight-bold'],
          }}
        >
          <FormattedMessage
            id="myAccount.billingAddressTitle"
            defaultMessage="Billing address"
          />
        </h5>
        {renderAddressInfo(address)}
      </>
    );
  };

  const renderAddressInfo = (address) => {
    return (
      <ul
        className="MyAccountOrder__address-info"
        style={{
          listStyle: 'none',
          paddingLeft: '0',
        }}
      >
        <li>{address.name}</li>
        {address.company && <li>{address.company}</li>}
        {address.care_of && <li>{address.care_of}</li>}
        <li>{address.street_address}</li>
        {address.street_address2 && <li>{address.street_address2}</li>}
        <li>
          {address.postalcode} {address.city}
        </li>
        {address.country_id && (
          <li>{countryStore.findCountry(address.country_id).name}</li>
        )}
      </ul>
    );
  };

  const renderStatusHeaderCol = (label, sizes) => {
    return (
      <Col
        className="MyAccountOrder__status-header"
        style={{
          fontSize: styles['font-size-lg'],
          fontWeight: styles['font-weight-bold'],
        }}
        {...sizes}
      >
        {label}
      </Col>
    );
  };

  const renderStatusHeaderRow = () => {
    return (
      <Row className="MyAccountOrder__status-headers">
        {renderStatusHeaderCol(StatusHeaders.date, { md: 3, xl: 2 })}
        {renderStatusHeaderCol(StatusHeaders.status, { md: 4, xl: 3 })}
        {renderStatusHeaderCol(StatusHeaders.description, { md: 5 })}
      </Row>
    );
  };

  const renderTotals = (visibleTotalClasses) => {
    const order = getOrder();

    return (
      <div className="MyAccountOrder__totals">
        {order.allTotals
          .filter((total) => {
            return visibleTotalClasses.includes(total.class);
          })
          .map((totalsItem) => (
            <Row key={totalsItem.name}>
              <Col xs="8" className="MyAccountOrder__total-item">
                {totalsItem.name}
              </Col>
              <Col xs="4" className="MyAccountOrder__total-value">
                <Price price={totalsItem.text} />
              </Col>
            </Row>
          ))}
      </div>
    );
  };

  const renderPaymentLogo = () => {
    const order = getOrder();
    if (order.payment.method && order.payment.method.logo) {
      return (
        <img
          src={order.payment.method.logo}
          alt={order.payment.method.name}
          loading="lazy"
          style={{
            maxWidth: '70px',
          }}
        />
      );
    }
    return null;
  };

  const renderShippingLogo = () => {
    const order = getOrder();
    if (order.shipping.method && order.shipping.method.logo) {
      return (
        <img
          src={order.shipping.method.logo}
          alt={order.shipping.method.name}
          loading="lazy"
          style={{
            maxWidth: '70px',
          }}
        />
      );
    }
    return null;
  };

  const renderInfoComponent = (title, content) => {
    return (
      <>
        <h5
          className="MyAccountOrder__detail-header"
          style={{
            fontWeight: styles['font-weight-bold'],
          }}
        >
          {title}
        </h5>
        <p>{content}</p>
      </>
    );
  };

  const renderPickUpPoint = (pickUpPoint) => {
    return (
      <div
        className="MyAccountOrder__detail-pickup"
        style={{
          marginBottom: styles['spacer'],
        }}
      >
        <div className="MyAccountOrder__detail-pickup-name">
          {pickUpPoint.name}
        </div>
        <div className="MyAccountOrder__detail-pickup-address">
          {pickUpPoint.address}
        </div>
        <div
          className="MyAccountOrder__detail-pickup-contact-info"
          style={{
            fontSize: styles['font-size-sm'],
          }}
        >
          {pickUpPoint.contact_info}
        </div>
      </div>
    );
  };

  const renderTracking = (shipping) => {
    const tracking = shipping.tracking;
    const hasTrackingInfo = shipping.hasTrackingInfo;

    return (
      <>
        <h5
          className="MyAccountOrder__detail-header"
          style={{
            fontWeight: styles['font-weight-bold'],
          }}
        >
          <FormattedMessage
            id="myAccount.trackingTitle"
            defaultMessage="Tracking order parcels"
          />
        </h5>
        {tracking.map((trackingOrder, index) => {
          return (
            <p key={trackingOrder.code}>
              <FormattedMessage
                id="myAccount.packageTitle"
                defaultMessage="Packet {parcelCount}: "
                values={{
                  parcelCount: index + 1,
                }}
              />{' '}
              {trackingOrder.carrier && (
                <span className="MyAccountOrder__detail-carrier">
                  {trackingOrder.carrier} {' - '}
                </span>
              )}
              <span className="MyAccountOrder__detail-code">
                {trackingOrder.code}
              </span>
              {trackingOrder.url && (
                <span className="MyAccountOrder__detail-link-available">
                  {' - '}
                  <a href={trackingOrder.url} target="_blank" rel="noopener">
                    <FormattedMessage
                      id="myAccount.packageToTracking"
                      defaultMessage="Go to tracking"
                    />{' '}
                    <Icon name="angle-double-right" />
                  </a>
                </span>
              )}
              {!trackingOrder.url && (
                <span className="MyAccountOrder__detail-link-unavailable">
                  {' - '}
                  <FormattedMessage
                    id="myAccount.noTracking"
                    defaultMessage="Tracking URL not available"
                  />
                </span>
              )}
            </p>
          );
        })}
        {hasTrackingInfo && (
          <FormattedMessage
            id="myAccount.trackingDescription"
            defaultMessage="(If the tracking page notifies you that no orders were found, it may be that the parcel is not yet received at the delivery company)"
          />
        )}
      </>
    );
  };

  const shouldRenderPaymentAddress = () => {
    const order = getOrder();

    return !isEqual(order.shipping.address, order.payment.address);
  };

  const renderMessage = () => {
    let message = null;

    switch (queryStatus) {
      case QueryStatus.NOT_FOUND:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="order.orderNotFound"
              defaultMessage="Given code is not found"
            />
          </Alert>
        );
        break;
      case QueryStatus.UNEXPECTED_ERROR:
        message = <UnexpectedError />;
        break;
      case QueryStatus.NONE:
      case QueryStatus.LOADING:
      case QueryStatus.LOADED:
      default:
        break;
    }

    return message;
  };

  const orderId = getCurrentOrderId();

  const Wrapper = ({ children }) => {
    return !configStore.account.enableMyAccount &&
      ((!accountStore.loggedIn && code) || accountStore.loggedIn) ? (
      <Container className="MyAccountPage__container">{children}</Container>
    ) : (
      children
    );
  };

  return (
    <Wrapper>
      <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
      <SEOTitle
        title={{
          ...messages['myAccount.orderTitle'],
          values: { orderId },
        }}
      />
      {queryStatus && renderMessage()}
      <ContentForState
        state={queryStatus}
        forLoaded={() => {
          const order = getOrder();
          return (
            <div className="MyAccountOrder">
              <PageTitle
                className="MyAccountOrder__page-title"
                style={{
                  display: 'flex',
                }}
              >
                <FormattedMessage
                  {...messages['myAccount.orderTitle']}
                  values={{ orderId }}
                />{' '}
                <span
                  className="MyAccountOrder__status"
                  style={{
                    marginLeft: styles['spacer-2'],
                  }}
                >
                  <OrderStatusLabel status={order.last_status} />
                </span>
              </PageTitle>
              <Container
                className="MyAccountOrder__container"
                style={{
                  paddingLeft: '0',
                  paddingRight: '0',
                }}
              >
                <Row>
                  <Col xl="6" md="12">
                    <Card
                      style={{
                        marginTop: styles['spacer-2'],
                        marginBottom: styles['spacer'],
                      }}
                    >
                      <CardHeader
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: styles['font-size-lg'],
                        }}
                      >
                        <FormattedMessage
                          id="myAccount.shippingDetailsTitle"
                          defaultMessage="Shipping details"
                        />
                        <FormattedMessage
                          id="myAccount.orderDate"
                          defaultMessage="Ordered on: {orderDate}"
                          values={{
                            orderDate: DateTime.fromISO(
                              order.date_purchased
                            ).toLocaleString(),
                          }}
                        />
                      </CardHeader>
                      <CardBody>
                        <Container
                          className="MyAccountOrder__shipping-details-container"
                          style={{
                            paddingLeft: '0',
                            paddingRight: '0',
                          }}
                        >
                          <Row>
                            <Col>
                              <h5
                                className="MyAccountOrder__detail-header"
                                style={{
                                  fontWeight: styles['font-weight-bold'],
                                }}
                              >
                                <FormattedMessage
                                  id="myAccount.shippingAddressTitle"
                                  defaultMessage="Shipping address"
                                />
                              </h5>
                              {renderAddressInfo(order.shipping.address)}
                              {order.seller_name &&
                                renderInfoComponent(
                                  <FormattedMessage
                                    {...globalTranslations.retailerTitle}
                                  />,
                                  order.seller_name
                                )}
                              {order.your_reference &&
                                renderInfoComponent(
                                  <FormattedMessage
                                    {...globalTranslations.yourReferenceTitle}
                                  />,
                                  order.your_reference
                                )}
                            </Col>
                            <Col>
                              <h5
                                className="MyAccountOrder__detail-header"
                                style={{
                                  fontWeight: styles['font-weight-bold'],
                                }}
                              >
                                <FormattedMessage
                                  id="myAccount.shippingMethodTitle"
                                  defaultMessage="Shipping method"
                                />
                              </h5>
                              <div
                                className="MyAccountOrder__method"
                                style={{
                                  marginBottom: styles['spacer-2'],
                                  display: 'flex',
                                }}
                              >
                                <div
                                  className="MyAccountOrder__method-logo"
                                  style={{
                                    maxWidth: '70px',
                                  }}
                                >
                                  {renderShippingLogo()}
                                </div>
                                <div className="MyAccountOrder__method-name">
                                  {order.shipping.method.name}
                                </div>
                              </div>
                              {order.shipping.pick_up_point &&
                                renderPickUpPoint(order.shipping.pick_up_point)}
                              {order.weight &&
                                renderInfoComponent(
                                  <FormattedMessage
                                    {...globalTranslations.weightTitle}
                                  />,
                                  order.weight
                                )}
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              {order.shipping.hasTrackingInfo &&
                                renderTracking(order.shipping)}
                            </Col>
                          </Row>
                        </Container>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl="6" md="12">
                    <Card
                      style={{
                        marginTop: styles['spacer-2'],
                        marginBottom: styles['spacer'],
                      }}
                    >
                      <CardHeader
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: styles['font-size-lg'],
                        }}
                      >
                        <FormattedMessage
                          id="myAccount.paymentDetailsTitle"
                          defaultMessage="Payment details"
                        />
                        {order.receipt_url && (
                          <Button
                            tag="a"
                            href={order.receipt_url}
                            color="primary"
                            className="MyAccountOrder__payment-receipt-button"
                            target="_blank"
                            style={{
                              marginTop: `-${styles['spacer-4']}`,
                              marginBottom: `-${styles['spacer-3']}`,
                            }}
                          >
                            <Icon name="print" />{' '}
                            <FormattedMessage
                              id="myAccount.printReceiptSentence"
                              defaultMessage="Print a receipt of this order"
                            />
                          </Button>
                        )}
                      </CardHeader>
                      <CardBody>
                        <Container
                          className="MyAccountOrder__payment-details-container"
                          style={{
                            paddingLeft: '0',
                            paddingRight: '0',
                          }}
                        >
                          <Row>
                            <Col xs="12" md="6">
                              {shouldRenderPaymentAddress() &&
                                renderPaymentAddressInfo(order.payment.address)}

                              <h5
                                className="MyAccountOrder__detail-header"
                                style={{
                                  fontWeight: styles['font-weight-bold'],
                                }}
                              >
                                <FormattedMessage
                                  id="myAccount.billingMethodTitle"
                                  defaultMessage="Billing method"
                                />
                              </h5>
                              <div
                                className="MyAccountOrder__method"
                                style={{
                                  marginBottom: styles['spacer-2'],
                                  display: 'flex',
                                }}
                              >
                                <div
                                  className="MyAccountOrder__method-logo"
                                  style={{
                                    maxWidth: '70px',
                                  }}
                                >
                                  {renderPaymentLogo()}
                                </div>
                                <div className="MyAccountOrder__method-name">
                                  {order.payment.method.name}
                                </div>
                              </div>
                            </Col>
                            <Col xs="12" md="6">
                              {renderTotals(billingInfoTotals)}
                            </Col>
                          </Row>
                        </Container>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <MyAccountOrderProducts order={order} />
                <Row>
                  <Col>
                    <Card
                      style={{
                        marginTop: styles['spacer-2'],
                        marginBottom: styles['spacer'],
                      }}
                    >
                      <CardHeader
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: styles['font-size-lg'],
                        }}
                      >
                        <FormattedMessage
                          id="myAccount.orderHistoryTitle"
                          defaultMessage="Shipping history"
                        />
                      </CardHeader>
                      <CardBody>
                        {!uiStore.isMobile && renderStatusHeaderRow()}
                        {order.status_history.map((entry, index) => (
                          <Row
                            key={`${entry.date}_${entry.status}_${index}`}
                            className="MyAccountOrder__status-history-entry"
                            style={{
                              padding: `${styles['spacer']} 0`,
                              borderTop: `1px solid #eee`,
                            }}
                          >
                            <Col md="3" xl="2">
                              {DateTime.fromISO(entry.date).toLocaleString(
                                DateTime.DATETIME_SHORT
                              )}
                            </Col>
                            <Col md="4" xl="3">
                              <OrderStatusLabel status={entry.status} />
                            </Col>
                            <Col md="5">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: entry.message,
                                }}
                              />
                            </Col>
                          </Row>
                        ))}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Container>
            </div>
          );
        }}
      />
    </Wrapper>
  );
};

MyAccountOrder.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  code: PropTypes.string,
};

export default injectIntl(
  inject(
    'accountStore',
    'configStore',
    'countryStore',
    'orderStore',
    'routeService',
    'uiStore'
  )(observer(MyAccountOrder))
);
